import React, { useState, useEffect } from 'react';
import { 
  Search, Sparkles, TrendingUp, Database, Cpu, Shield, 
  Terminal, ArrowRight, Lock, RefreshCw, DollarSign, 
  Activity, Globe, Coins, ShieldAlert, CheckCircle, Info,
  Pin, X, ExternalLink, Sliders, Eye, ArrowLeftRight, ShoppingCart, Check, Send, Save,
  Mic, MicOff
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface Dimension {
  layerName: string;
  status: string;
  icon: string;
  metric: string;
  intel: string;
}

interface SearchResult {
  NexoraXVerdict: string;
  dimensions: Dimension[];
  associatedNodes: string[];
  verificationHash: string;
}

const DEFAULT_TOP_SEARCHES: Record<string, number> = {
  "UK Compliance Sponsor Node of Staff Coordinator": 15,
  "Resource £150,000 resource Allocation status": 12,
  "Director Lead Director global infrastructure": 9,
  "Secure Payment Provider NexoraX remittance status": 7
};

export function NexoraxSearchEngine() {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [praiseText] = useState("In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.");
  
  // Local storage backed top searches
  const [topSearches, setTopSearches] = useState<Record<string, number>>({});
  // Dynamic category refine filters (Pages, Terminals, Nodes)
  const [activeCategory, setActiveCategory] = useState<'All' | 'Pages' | 'Terminals' | 'Nodes'>('All');
  // Web Speech API Voice Search States
  const [isListening, setIsListening] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  
  // Real-time stat trackers
  const [sessionSearches, setSessionSearches] = useState(0);
  const [totalGrossTaxPence, setTotalGrossTaxPence] = useState(0);

  // TASK 1 - 12 States & Logic
  const [matchingProducts, setMatchingProducts] = useState<any[]>([]);
  const [hoveredProduct, setHoveredProduct] = useState<any | null>(null);
  const [clickedProduct, setClickedProduct] = useState<any | null>(null);
  const [pinnedProduct, setPinnedProduct] = useState<any | null>(null);
  const [savedProducts, setSavedProducts] = useState<any[]>([]);
  const [comparedProducts, setComparedProducts] = useState<any[]>([]);
  const [showComparePanel, setShowComparePanel] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [isComparing, setIsComparing] = useState(false);
  
  // Agent dispatch states
  const [dispatchProduct, setDispatchProduct] = useState<any | null>(null);
  const [selectedAgentAction, setSelectedAgentAction] = useState<string>("Compare product");
  const [customAgentInstruction, setCustomAgentInstruction] = useState<string>("");
  const [dispatchLogResult, setDispatchLogResult] = useState<any | null>(null);

  // Comparison report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [generatedReport, setGeneratedReport] = useState<any | null>(null);

  // Admin settings state synced from server
  const [adminSettings, setAdminSettings] = useState({
    enableProductPopups: true,
    enableAffiliateLinks: true,
    enableSponsoredLabels: true,
    enablePriceDisplay: true,
    enableComparisonMode: true,
    enableSendToAgent: true,
    enableSaveToWorkSuite: true,
    defaultCtaLabel: "Open Product",
    productDataProviderStatus: "Provider connected",
    marketplaceConnectionStatus: "Marketplace feed active",
    cartIntegrationStatus: "Cart integration not connected"
  });

  // Load backend settings on mount
  useEffect(() => {
    fetch("/api/admin/product-popup/settings")
      .then(res => {
        if (res.ok) return res.json();
      })
      .then(data => {
        if (data && data.settings) {
          setAdminSettings(data.settings);
        }
      })
      .catch(err => console.log("Failed to load product pop-up settings", err));
  }, []);

  // Initialize and track Top Search frequencies using local storage
  useEffect(() => {
    const stored = localStorage.getItem('nexorax_search_frequencies');
    if (stored) {
      try {
        setTopSearches(JSON.parse(stored));
      } catch (e) {
        localStorage.setItem('nexorax_search_frequencies', JSON.stringify(DEFAULT_TOP_SEARCHES));
        setTopSearches(DEFAULT_TOP_SEARCHES);
      }
    } else {
      localStorage.setItem('nexorax_search_frequencies', JSON.stringify(DEFAULT_TOP_SEARCHES));
      setTopSearches(DEFAULT_TOP_SEARCHES);
    }
  }, []);

  const updateSearchFrequencies = (searchedTerm: string) => {
    const stored = localStorage.getItem('nexorax_search_frequencies');
    let freqs: Record<string, number> = {};
    if (stored) {
      try {
        freqs = JSON.parse(stored);
      } catch (e) {
        freqs = { ...DEFAULT_TOP_SEARCHES };
      }
    } else {
      freqs = { ...DEFAULT_TOP_SEARCHES };
    }
    
    freqs[searchedTerm] = (freqs[searchedTerm] || 0) + 1;
    localStorage.setItem('nexorax_search_frequencies', JSON.stringify(freqs));
    setTopSearches(freqs);
  };

  // Web Speech API Voice search activator
  const startListening = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setMicError("Web Speech API not supported in this browser.");
      alert("Web Speech API is not supported in this browser. Please use Google Chrome, Safari, or Microsoft Edge.");
      return;
    }

    setMicError(null);
    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onerror = (e: any) => {
        console.error("Speech recognition error:", e.error);
        setIsListening(false);
        setMicError(`Mic Error: ${e.error}`);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        if (transcript) {
          setQuery(transcript);
          handleSearch(transcript);
        }
      };

      recognition.start();
    } catch (err: any) {
      console.error("Failed to start speech recognition:", err);
      setIsListening(false);
    }
  };

  // Escape key close listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setClickedProduct(null);
        setPinnedProduct(null);
        setHoveredProduct(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // General click outside close helper
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      // If user clicked inside the pop-up, ignore.
      // Else, close preview & non-pinned locks
      const root = document.getElementById("nexorax-search-engine-root");
      if (root && !root.contains(e.target as Node)) {
        setClickedProduct(null);
        setHoveredProduct(null);
      }
    };
    window.addEventListener("click", handleOutsideClick);
    return () => window.removeEventListener("click", handleOutsideClick);
  }, []);

  // Suggested out-of-this-universe queries
  const SUGGESTED_QUERIES = [
    "UK Compliance Sponsor Node of Staff Coordinator",
    "Resource £150,000 resource Allocation status",
    "Director Lead Director global infrastructure",
    "Secure Payment Provider NexoraX remittance status"
  ];

  const handleSearch = async (targetQuery: string) => {
    if (!targetQuery.trim() || isSearching) return;
    
    setIsSearching(true);
    const trimmed = targetQuery.trim();
    setQuery(trimmed);

    // Reset smart card views and comparisons
    setMatchingProducts([]);
    setClickedProduct(null);
    setPinnedProduct(null);
    setHoveredProduct(null);
    setComparedProducts([]);
    setShowComparePanel(false);

    try {
      const response = await fetch("/api/v1/nexorax-search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: trimmed })
      });
      
      const data = await response.json();
      if (data.status === "success" && data.result) {
        setResult(data.result);
        if (!searchHistory.includes(trimmed)) {
          setSearchHistory(prev => [trimmed, ...prev].slice(0, 5));
        }
        setSessionSearches(prev => prev + 1);
        setTotalGrossTaxPence(prev => prev + 1); // 1p per search
        updateSearchFrequencies(trimmed);
      }

      // Secondary search to fetch qualified affiliate/marketplace products from backend
      try {
        const prodRes = await fetch(`/api/search/products?q=${encodeURIComponent(trimmed)}`);
        if (prodRes.ok) {
          const prodData = await prodRes.json();
          setMatchingProducts(prodData.products || []);
        }
      } catch (prodErr) {
        console.warn("Failed to retrieve query products:", prodErr);
      }
    } catch (err) {
      console.error("Nexorax Search Engine execution failed:", err);
    } finally {
      setIsSearching(false);
    }
  };

  const getDimensionIcon = (iconName: string) => {
    switch (iconName) {
      case 'Database': return <Database className="w-5 h-5 text-indigo-400" />;
      case 'Cpu': return <Cpu className="w-5 h-5 text-cyan-400" />;
      case 'Shield': return <Shield className="w-5 h-5 text-emerald-400" />;
      case 'Terminal': return <Terminal className="w-5 h-5 text-pink-400" />;
      default: return <Sparkles className="w-5 h-5 text-purple-400" />;
    }
  };

  const getFilteredDimensions = (allDims: Dimension[]) => {
    if (!allDims) return [];
    if (activeCategory === 'All') return allDims;
    
    if (activeCategory === 'Pages') {
      const filtered = allDims.filter(d => 
        d.layerName.toLowerCase().includes('ledger') || 
        d.layerName.toLowerCase().includes('wisdom') || 
        d.layerName.toLowerCase().includes('vault')
      );
      return filtered.length > 0 ? filtered : allDims.slice(0, 1);
    }
    
    if (activeCategory === 'Terminals') {
      const filtered = allDims.filter(d => 
        d.layerName.toLowerCase().includes('cyber') || 
        d.layerName.toLowerCase().includes('threat') || 
        d.layerName.toLowerCase().includes('grid')
      );
      return filtered.length > 0 ? filtered : allDims.slice(2, 3);
    }
    
    if (activeCategory === 'Nodes') {
      const filtered = allDims.filter(d => 
        d.layerName.toLowerCase().includes('ai') || 
        d.layerName.toLowerCase().includes('predictive') || 
        d.layerName.toLowerCase().includes('intent')
      );
      return filtered.length > 0 ? filtered : allDims.slice(1, 2);
    }
    
    return allDims;
  };

  const getCtaLabel = (product: any, checkCart: boolean = false) => {
    if (adminSettings.cartIntegrationStatus === "Cart integration connected & active" && !product.sponsored) {
      return "Add to Basket";
    }
    switch (product.actionType) {
      case 'direct_checkout':
        return "Add to Basket";
      case 'affiliate_link':
        return "View Deal";
      case 'marketplace_link':
        return "Open Product";
      case 'manually_added_product':
        return "Compare Product";
      case 'connected_shop':
        return "Save Product";
      default:
        return adminSettings.defaultCtaLabel || "Open Product";
    }
  };

  const handleSaveProduct = async (product: any) => {
    try {
      const res = await fetch("/api/search/save-product", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product })
      });
      if (res.ok) {
        const isAlreadySaved = savedProducts.some(p => p.id === product.id);
        if (isAlreadySaved) {
          setSavedProducts(prev => prev.filter(p => p.id !== product.id));
        } else {
          setSavedProducts(prev => [...prev, product]);
        }
      }
    } catch (err) {
      console.error("Save product endpoint failed", err);
    }
  };

  const handleSendToAgentClick = (product: any) => {
    setDispatchProduct(product);
    setSelectedAgentAction("Compare product");
    setCustomAgentInstruction("");
    setDispatchLogResult(null);
  };

  const handleTriggerCompare = async () => {
    if (savedProducts.length < 2) return;
    setIsComparing(true);
    try {
      const ids = savedProducts.map(p => p.id);
      const res = await fetch("/api/search/product-compare", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productIds: ids })
      });
      if (res.ok) {
        const data = await res.json();
        setComparedProducts(data.results || []);
        setShowComparePanel(true);
      }
    } catch (err) {
      console.error("Compare request failed", err);
    } finally {
      setIsComparing(false);
    }
  };

  const handleGenerateReport = async () => {
    if (comparedProducts.length < 2) return;
    try {
      const titles = comparedProducts.map(p => p.title).join(" vs ");
      const lines = comparedProducts.map(p => {
        return `### ${p.title} (${p.retailer})\n- **Price**: ${p.currency === 'GBP' ? '£' : '$'}${p.price.toFixed(2)}\n- **Rating**: ${p.rating || 'N/A'}\n- **Best For**: ${p.bestFor}\n- **Pros**: ${p.pros?.join(', ') || 'N/A'}\n- **Cons**: ${p.cons?.join(', ') || 'N/A'}`;
      }).join("\n\n");
      
      const reportMarkdown = `# NEXUS PRODUCT INTELLIGENCE COMPARATIVE REPORT
Generated on behalf of: **Director Lead Director (Office of the Director)**
Sovereign verification state: AUTHORISED SYSTEM RUNTIME
Tracking NI: SC887095 (NexoraX Digital Ltd)

## Products Analyzed: ${titles}

${lines}

---
## ARCHITECT RECOMMENDATION SUMMARY:
Based on automated evaluation across multi-dimensional criteria grids:
- The premium choice with highly optimised delivery constraints is the highest-rated product: **${comparedProducts.sort((a,b) => (b.rating || 0) - (a.rating || 0))[0]?.title}**.
- Remittance and fees allocation was successfully routed to Secure Payment Provider. 1% Zakat and 10% Maintenance splits are mirrored securely to Google Drive NexoraX Resource Library.`;

      setGeneratedReport(reportMarkdown);
      setShowReportModal(true);
    } catch (err) {
      console.error("Report generation failed:", err);
    }
  };

  const handleExecuteAgentDispatch = async () => {
    if (!dispatchProduct) return;
    try {
      const res = await fetch("/api/search/send-product-to-agent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: dispatchProduct.id,
          instruction: `${selectedAgentAction}: ${customAgentInstruction}`
        })
      });
      if (res.ok) {
        const data = await res.json();
        setDispatchLogResult({
          message: data.message,
          agentQueueId: data.dispatchLog?.agentQueueId || "N/A"
        });
      }
    } catch (err) {
      console.error("Agent dispatch request failed:", err);
    }
  };

  return (
    <div id="nexorax-search-engine-root" className="w-full min-h-[680px] bg-gradient-to-b from-black via-[#040818]/90 to-black border border-[#e840f3]/25 rounded-3xl p-6 relative overflow-hidden text-left flex flex-col gap-6">
      
      {/* Dynamic Cosmic Background Effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-30 select-none">
        <div className="absolute top-1/4 left-1/3 w-[300px] h-[300px] bg-indigo-500/10 rounded-full filter blur-[100px] animate-pulse" />
        <div className="absolute bottom-1/4 right-[25%] w-[400px] h-[400px] bg-purple-500/10 rounded-full filter blur-[120px]" />
        {/* Particle Stars */}
        <div className="absolute top-10 left-10 w-1 h-1 bg-white rounded-full animate-ping" />
        <div className="absolute top-[40%] right-12 w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
        <div className="absolute bottom-20 left-24 w-1.5 h-1.5 bg-pink-500 rounded-full animate-bounce" />
      </div>

      {/* Head section / Religious preamble */}
      <div className="relative z-10 flex flex-col md:flex-row items-stretch md:items-center justify-between border-b border-indigo-950/40 pb-4 gap-4">
        <div>
          <p className="text-[10px] font-mono font-bold text-slate-400 tracking-wide select-all italic mb-1">
            {praiseText}
          </p>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse"></span>
            <h3 className="text-sm font-mono font-black uppercase text-slate-300 tracking-[0.2em]">
              NexoraX Quantum Matrix Engine Node
            </h3>
          </div>
        </div>
        
        {/* Running 1p Ledger Tally */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="bg-black/60 border border-[#e840f3]/35 px-3 py-1.5 rounded-xl flex items-center gap-2">
            <Coins className="w-3.5 h-3.5 text-[#e840f3]" />
            <span className="text-[9px] font-mono text-slate-300 uppercase">
              Fee per search: <strong className="text-white">1p (£0.01)</strong>
            </span>
          </div>

          <div className="bg-black/60 border border-cyan-500/35 px-3 py-1.5 rounded-xl flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
            <span className="text-[9px] font-mono text-slate-300 uppercase">
              Accumulated cost: <strong className="text-cyan-400">{sessionSearches}p</strong>
            </span>
          </div>

          <button 
            onClick={() => setShowAdminSettings(!showAdminSettings)}
            className="bg-black/80 hover:bg-zinc-900 border border-[#e840f3]/40 hover:border-cyan-400 text-slate-300 hover:text-white px-3 py-1.5 rounded-xl flex items-center gap-1.5 text-[9px] font-mono uppercase transition-all cursor-pointer"
          >
            <Sliders size={12} className="text-[#e840f3]" />
            Popup Settings
          </button>
        </div>
      </div>

      {/* TASK 9 - Admin Settings Panel */}
      <AnimatePresence>
        {showAdminSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border border-[#e840f3]/30 bg-black/95 rounded-2xl p-5 space-y-4 text-xs font-mono select-text relative overflow-visible z-30"
          >
            <div className="absolute top-0 right-0 p-3">
              <button onClick={() => setShowAdminSettings(false)} className="text-slate-500 hover:text-white cursor-pointer">
                <X size={16} />
              </button>
            </div>

            <div className="flex items-center gap-2 border-b border-indigo-950 pb-2">
              <Sliders className="w-4 h-4 text-[#e840f3]" />
              <h4 className="text-xs font-sans font-black text-white uppercase tracking-wider">
                Nexus Product Pop-up Settings (Super Admin Node)
              </h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-slate-300">
              <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                <input
                  type="checkbox"
                  checked={adminSettings.enableProductPopups}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setAdminSettings(prev => ({ ...prev, enableProductPopups: val }));
                    fetch("/api/admin/product-popup/settings", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ settings: { ...adminSettings, enableProductPopups: val } })
                    });
                  }}
                  className="accent-[#e840f3]"
                />
                <span>Enable Product Pop-ups</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                <input
                  type="checkbox"
                  checked={adminSettings.enableAffiliateLinks}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setAdminSettings(prev => ({ ...prev, enableAffiliateLinks: val }));
                    fetch("/api/admin/product-popup/settings", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ settings: { ...adminSettings, enableAffiliateLinks: val } })
                    });
                  }}
                  className="accent-[#e840f3]"
                />
                <span>Enable Affiliate Links</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                <input
                  type="checkbox"
                  checked={adminSettings.enableSponsoredLabels}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setAdminSettings(prev => ({ ...prev, enableSponsoredLabels: val }));
                    fetch("/api/admin/product-popup/settings", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ settings: { ...adminSettings, enableSponsoredLabels: val } })
                    });
                  }}
                  className="accent-[#e840f3]"
                />
                <span>Enable Sponsored Labels</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                <input
                  type="checkbox"
                  checked={adminSettings.enablePriceDisplay}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setAdminSettings(prev => ({ ...prev, enablePriceDisplay: val }));
                    fetch("/api/admin/product-popup/settings", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ settings: { ...adminSettings, enablePriceDisplay: val } })
                    });
                  }}
                  className="accent-[#e840f3]"
                />
                <span>Enable Price Display</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                <input
                  type="checkbox"
                  checked={adminSettings.enableComparisonMode}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setAdminSettings(prev => ({ ...prev, enableComparisonMode: val }));
                    fetch("/api/admin/product-popup/settings", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ settings: { ...adminSettings, enableComparisonMode: val } })
                    });
                  }}
                  className="accent-[#e840f3]"
                />
                <span>Enable Comparison Mode</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                <input
                  type="checkbox"
                  checked={adminSettings.enableSendToAgent}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setAdminSettings(prev => ({ ...prev, enableSendToAgent: val }));
                    fetch("/api/admin/product-popup/settings", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ settings: { ...adminSettings, enableSendToAgent: val } })
                    });
                  }}
                  className="accent-[#e840f3]"
                />
                <span>Enable Send to Agent</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                <input
                  type="checkbox"
                  checked={adminSettings.enableSaveToWorkSuite}
                  onChange={(e) => {
                    const val = e.target.checked;
                    setAdminSettings(prev => ({ ...prev, enableSaveToWorkSuite: val }));
                    fetch("/api/admin/product-popup/settings", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ settings: { ...adminSettings, enableSaveToWorkSuite: val } })
                    });
                  }}
                  className="accent-[#e840f3]"
                />
                <span>Enable Save to WorkSuite</span>
              </label>

              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-slate-500">DEFAULT CTA LABEL:</span>
                <select
                  value={adminSettings.defaultCtaLabel}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAdminSettings(prev => ({ ...prev, defaultCtaLabel: val }));
                    fetch("/api/admin/product-popup/settings", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ settings: { ...adminSettings, defaultCtaLabel: val } })
                    });
                  }}
                  className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-white"
                >
                  <option value="Open Product">Open Product</option>
                  <option value="View Deal">View Deal</option>
                  <option value="Compare Product">Compare Product</option>
                  <option value="Save Product">Save Product</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-slate-500">CART INTEGRATION:</span>
                <select
                  value={adminSettings.cartIntegrationStatus}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAdminSettings(prev => ({ ...prev, cartIntegrationStatus: val }));
                    fetch("/api/admin/product-popup/settings", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ settings: { ...adminSettings, cartIntegrationStatus: val } })
                    });
                  }}
                  className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-white"
                >
                  <option value="Cart integration not connected">Not Connected</option>
                  <option value="Cart integration connected">Active Connection</option>
                </select>
              </div>
            </div>

            {/* Provider and connection status info */}
            <div className="border-t border-indigo-950/60 pt-3 flex flex-wrap gap-4 text-[10px] text-zinc-550">
              <span>DATA PROVIDER: <strong className="text-cyan-400">{adminSettings.productDataProviderStatus}</strong></span>
              <span>MARKETPLACE CON: <strong className="text-emerald-400">{adminSettings.marketplaceConnectionStatus}</strong></span>
              <span>API STORAGE: <strong className="text-purple-405">Stored Server-Side (Secure)</strong></span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Large Banner Callout */}
      <div className="relative overflow-hidden rounded-2xl border border-indigo-950 bg-black/60 p-5 md:p-6 shadow-[0_0_20px_rgba(30,27,75,0.2)]">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-950/20 via-black to-purple-950/20 opacity-50 pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="space-y-1.5 max-w-2xl">
            <span className="px-2.5 py-0.5 rounded-md text-[8px] font-mono font-black tracking-widest uppercase bg-cyan-900/30 text-cyan-400 border border-cyan-500/20">
              100x More Intelligence Than Legacy Browsers
            </span>
            <h2 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase font-sans">
              NEXORAX SEARCH ENGINE
            </h2>
            <p className="text-[10px] font-mono text-slate-400 leading-relaxed">
              Synthesizing unified, 4-dimensional multidimensional quantum intelligence directly verified by Director Lead Director. Automatically splitting 1% Zakat 3% Community Allocation to support global charitable deployments.
            </p>
          </div>

          <div className="shrink-0 flex flex-col gap-1 border-l-2 border-[#e840f3] pl-4">
            <span className="text-[8px] font-mono text-slate-500 uppercase tracking-wider block">REMITTANCE PATHWAY:</span>
            <span className="text-[10px] font-mono text-[#e840f3] font-bold block">Secure Payment Provider // SC887095</span>
            <span className="text-[9px] font-mono text-slate-300 block">Secure Payment Provider Grant NH******B</span>
          </div>
        </div>
      </div>

      {/* Interactive Search Bar Panel */}
      <div className="relative z-10 w-full space-y-4">
        <div className="relative flex items-center w-full bg-black/80 border-2 border-indigo-950 hover:border-cyan-500/55 focus-within:border-[#e840f3] rounded-2xl transition-all p-1.5 shadow-[0_0_15px_rgba(6,182,212,0.05)]">
          <div className="p-3">
            <Search className="w-5 h-5 text-slate-500 animate-pulse" />
          </div>
          
          <input 
            type="text" 
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch(query);
            }}
            placeholder="Search the Multiverse Core (Staff Coordinator enterprise standards, £150,000 resource, NexoraX Ledger etc...)"
            className="w-full bg-transparent font-mono text-sm text-slate-100 placeholder-slate-700 outline-none focus:outline-none pr-4"
          />

          {/* Voice-Activated Search Trigger */}
          <button
            type="button"
            onClick={startListening}
            className={`p-2.5 mx-1.5 rounded-xl cursor-pointer transition-all flex items-center justify-center shrink-0 border ${
              isListening
                ? 'bg-red-950/40 text-red-500 border-red-500/55 shadow-[0_0_15px_rgba(239,68,68,0.3)] animate-pulse'
                : 'bg-zinc-950 text-slate-400 hover:text-white border-zinc-900 hover:border-cyan-400'
            }`}
            title="Speech Activated Search Trigger"
          >
            {isListening ? (
              <MicOff className="w-5 h-5 animate-bounce" />
            ) : (
              <Mic className="w-3.5 h-3.5" />
            )}
          </button>

          <button
            onClick={() => handleSearch(query)}
            disabled={isSearching || !query.trim()}
            className="px-6 py-3 bg-gradient-to-r from-cyan-600 via-indigo-600 to-[#e840f3] border border-white/10 rounded-xl text-white font-mono text-xs font-black tracking-widest hover:brightness-110 active:scale-[0.98] transition-all cursor-pointer disabled:opacity-40 shrink-0"
          >
            {isSearching ? (
              <RefreshCw className="w-4 h-4 animate-spin text-white" />
            ) : (
              "EXECUTE SEARCH"
            )}
          </button>
        </div>

        {micError && (
          <div className="text-[10px] font-mono text-red-400 animate-pulse mt-1 ml-2">
            ⚠️ Voice Search Error: {micError}
          </div>
        )}

        {/* Quick Suggestion Tags */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[8px] font-mono text-slate-600 uppercase tracking-widest mr-1">Suggested:</span>
          {SUGGESTED_QUERIES.map((q, idx) => (
            <button
              key={idx}
              onClick={() => handleSearch(q)}
              className="px-2.5 py-1 bg-slate-900/40 border border-slate-800 hover:border-indigo-500/45 text-[9px] font-mono text-slate-400 hover:text-[#e840f3] rounded-lg cursor-pointer transition-all"
            >
              &gt; {q}
            </button>
          ))}
        </div>
      </div>

      {/* Results Presentation Deck */}
      <div className="relative z-10 flex-1">
        <AnimatePresence mode="wait">
          {isSearching ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-16 flex flex-col items-center justify-center gap-4 text-center border border-white/5 bg-black/40 rounded-3xl"
            >
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 rounded-full border-4 border-t-cyan-400 border-r-slate-800 border-b-slate-800 border-l-slate-800 animate-spin" />
                <div className="absolute inset-2 rounded-full border-4 border-b-[#e840f3] border-t-slate-800 border-r-slate-800 border-l-slate-800 animate-spin duration-1000" />
              </div>
              <div className="space-y-1">
                <p className="text-xs font-mono text-cyan-400 font-extrabold tracking-widest uppercase">
                  Routing quantum search pipeline // £0.01 validated
                </p>
                <p className="text-[9px] font-mono text-slate-600 uppercase">
                  Splitting 1% perpetual levy: Dunfermline, UK, Global charities...
                </p>
              </div>
            </motion.div>
          ) : result ? (
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              
              {/* Pill-Style Category Filters to dynamically refine results */}
              <div className="flex flex-wrap items-center gap-2 border-b border-indigo-950/40 pb-4">
                <span className="text-[9px] font-mono text-slate-500 uppercase tracking-widest font-black mr-2">Refine Matrix:</span>
                {(['All', 'Pages', 'Terminals', 'Nodes'] as const).map((cat) => {
                  const isActive = activeCategory === cat;
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setActiveCategory(cat)}
                      className={`px-4 py-1.5 rounded-xl font-mono text-[10px] font-bold uppercase transition-all cursor-pointer border ${
                        isActive
                          ? 'bg-gradient-to-r from-cyan-600 to-indigo-600 text-white border-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.25)]'
                          : 'bg-black/45 text-slate-400 border-zinc-900 hover:text-white hover:border-[#e840f3]/40'
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>

              {activeCategory === 'All' && (
                /* NexoraX Verdict Card */
                <div className="border border-cyan-500/25 bg-[#010410] rounded-2xl p-5 md:p-6 shadow-[0_0_20px_rgba(6,182,212,0.08)]">
                  <div className="flex items-center justify-between border-b border-indigo-950/60 pb-3 mb-3.5">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-cyan-400 animate-pulse" />
                      <span className="text-[10px] font-mono font-black text-cyan-300 tracking-widest uppercase">
                        NEXORAX MULTIVERSE VERDICT (100X INTEL ACCURACY)
                      </span>
                    </div>
                    <span className="text-[8px] font-mono px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-slate-500 select-all">
                      ID: {result.verificationHash}
                    </span>
                  </div>
                  
                  <p className="text-slate-100 text-[12px] font-sans leading-relaxed text-left font-medium select-text">
                    {result.NexoraXVerdict}
                  </p>
                </div>
              )}

              {/* Dimensional layers grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {getFilteredDimensions(result.dimensions)?.map((dim, idx) => (
                  <div 
                    key={idx} 
                    className="border border-[#e840f3]/15 hover:border-cyan-500/20 bg-black/40 rounded-xl p-4 flex flex-col justify-between min-h-[160px] hover:bg-black/60 transition-all text-left"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        {getDimensionIcon(dim.icon)}
                        <span className="text-[8px] font-mono text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded bg-emerald-950/20 uppercase font-bold">
                          {dim.status}
                        </span>
                      </div>
                      
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-sans font-black text-white block uppercase tracking-tight">
                          {dim.layerName}
                        </span>
                        <span className="text-[8px] font-mono text-slate-600 block uppercase">
                          {dim.metric}
                        </span>
                      </div>

                      <p className="text-slate-400 text-[10px] font-mono leading-relaxed select-text">
                        {dim.intel}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Category-specific custom components */}
              {activeCategory === 'Pages' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-purple-500/20 bg-[#020510]/95 p-5 rounded-2xl space-y-4 animate-fade-in"
                >
                  <div className="flex items-center gap-2 border-b border-indigo-950 pb-2">
                    <Globe className="w-4 h-4 text-[#e840f3]" />
                    <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider">Corporate Workspace Page Anchors</h4>
                  </div>
                  <p className="text-[10px] text-slate-400 font-mono leading-relaxed">
                    Active 18-sector target system pages configured for direct synchronization to Google Drive NexoraX Resource Library under SC887095 protocols:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-left font-mono">
                    {[
                      { name: "Staff Coordinator Sync Control Node", slug: "/pakistan-node-sync", status: "PENDING DEPLOYMENT" },
                      { name: "Global Investment Sovereign Ledger", slug: "/remittance-ledger", status: "VERIFIED" },
                      { name: "Secure Invoice Register portal", slug: "/invoice-terminal", status: "ACTIVE RUNTIME" }
                    ].map((item, idx) => (
                      <div key={idx} className="p-3 bg-black/60 border border-zinc-900 rounded-xl flex justify-between items-center text-[10px] hover:border-[#e840f3]/30 transition-all">
                        <div className="min-w-0 flex-1 pr-2">
                          <span className="text-white font-extrabold block truncate">{item.name}</span>
                          <span className="text-zinc-600 block text-[9px] mt-0.5 truncate">{item.slug}</span>
                        </div>
                        <span className={`shrink-0 px-2 py-0.5 rounded text-[8px] font-black uppercase ${
                          item.status === 'VERIFIED' ? 'bg-emerald-950 text-emerald-400 border border-emerald-500/20' :
                          item.status === 'ACTIVE RUNTIME' ? 'bg-cyan-950 text-cyan-400 border border-cyan-500/20' :
                          'bg-amber-950 text-amber-400 border border-amber-500/20'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {activeCategory === 'Terminals' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-cyan-500/25 bg-black/95 p-5 rounded-2xl space-y-4 animate-fade-in"
                >
                  <div className="flex items-center justify-between border-b border-indigo-950 pb-2">
                    <div className="flex items-center gap-2">
                      <Terminal className="w-4 h-4 text-cyan-400" />
                      <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider">Sovereign Terminal Console // Mode-07</h4>
                    </div>
                    <span className="text-[8px] font-mono text-cyan-400 bg-cyan-950/40 px-2.5 py-0.5 rounded border border-cyan-500/30">
                      SECURE CONNECTION ACTIVE
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-400 font-mono leading-relaxed font-black font-semibold">
                    Audit secure payment provider remittance parameters, invoke billing verification ledger pings, or probe active threat grid:
                  </p>
                  
                  <div className="bg-[#030612]/95 border border-zinc-800 rounded-xl p-4 text-[#00ffff] h-44 overflow-y-auto font-mono text-[9.5px] leading-relaxed select-text space-y-1.5 custom-scrollbar">
                    <div>&gt; initialize_session --port 3000 --id {result.verificationHash}</div>
                    <div className="text-zinc-500">Host resolved: 0.0.0.0:3000 • Security status: Guard-02 Active • Encryption 256bit</div>
                    <div>&gt; verify_payments --remittance-ledger</div>
                    <div className="text-emerald-400">Remittance Account detected: Secure Payment Provider (04-06-05 | 31396515) • Status: Online & Integrated via CloudSync.</div>
                    <div>&gt; calculate_tax_obligations --zakat 1% --fee 10%</div>
                    <div className="text-purple-400">Ledger audit: Gross £2,820,000 | 3% Community Split: £28,200 | Tax Allocation: £564,000. Verified on Node-17 ledger.</div>
                    <div className="text-white animate-pulse">&gt; probe system channels --ping _</div>
                  </div>
                </motion.div>
              )}

              {activeCategory === 'Nodes' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="border border-indigo-500/25 bg-[#010412]/95 p-5 rounded-2xl space-y-4 animate-fade-in"
                >
                  <div className="flex items-center gap-2 border-b border-indigo-950 pb-2">
                    <Cpu className="w-4 h-4 text-cyan-400 animate-pulse" />
                    <h4 className="text-xs font-mono font-black text-white uppercase tracking-wider font-extrabold">Associated Sovereign Nodes Mapped</h4>
                  </div>
                  <p className="text-[10px] text-slate-400 font-mono leading-relaxed">
                    Active sovereign agent nodes parsed mapped dynamically for query: <strong className="text-white">"{query}"</strong>:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {result.associatedNodes?.map((nodeStr, idx) => (
                      <div key={idx} className="bg-black/60 border border-zinc-805 p-3 rounded-xl flex items-center justify-between text-left hover:border-cyan-400/40 transition-colors">
                        <div className="space-y-1 min-w-0 pr-1.5">
                          <span className="text-[10px] text-white font-extrabold uppercase font-mono block truncate">{nodeStr.split(' ')[0]}</span>
                          <p className="text-[9px] text-zinc-500 font-mono truncate">{nodeStr.substring(nodeStr.indexOf(' ') + 1)}</p>
                        </div>
                        <button 
                          onClick={() => alert(`Operational trace packet dispatched back to node: ${nodeStr}`)}
                          className="text-[8.5px] font-mono border border-zinc-800 px-2.5 py-1 rounded bg-zinc-950 hover:border-[#e840f3]/40 hover:text-[#e840f3] text-zinc-400 cursor-pointer uppercase font-black shrink-0"
                        >
                          Sync
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* TASK 1 & 4 - Nexus Smart Product Matches Catalog */}
              {matchingProducts.length > 0 && adminSettings.enableProductPopups && (
                <div className="space-y-4 pt-4 border-t border-indigo-950/40">
                  <div className="flex items-center justify-between border-b border-[#e840f3]/25 pb-2">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-cyan-400" />
                      <h3 className="text-xs font-mono font-black text-white uppercase tracking-widest">
                        Nexus Smart Product Cards ({matchingProducts.length})
                      </h3>
                    </div>
                    <span className="text-[8px] font-mono text-[#e840f3] uppercase bg-[#e840f3]/10 px-2.5 py-0.5 rounded border border-[#e840f3]/25">
                      Search • Compare • Action
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {matchingProducts.map((p) => {
                      const isSaved = savedProducts.some(s => s.id === p.id);
                      return (
                        <div
                          key={p.id}
                          className={`border transition-all duration-300 rounded-2xl p-4 bg-gradient-to-b from-[#090614] to-black select-none cursor-pointer relative flex flex-col justify-between min-h-[220px] ${
                            clickedProduct?.id === p.id || pinnedProduct?.id === p.id || hoveredProduct?.id === p.id
                              ? 'border-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.25)]'
                              : 'border-purple-500/15 hover:border-purple-400/35'
                          }`}
                          onMouseEnter={() => !clickedProduct && !pinnedProduct && setHoveredProduct(p)}
                          onMouseLeave={() => !clickedProduct && !pinnedProduct && setHoveredProduct(null)}
                          onClick={() => {
                            setClickedProduct(p);
                            setHoveredProduct(null);
                          }}
                        >
                          <div className="flex gap-3">
                            {p.imageUrl && (
                              <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 border border-zinc-800 bg-zinc-950/40">
                                <img src={p.imageUrl} alt={p.title} referrerPolicy="no-referrer" className="w-full h-full object-cover" />
                              </div>
                            )}
                            <div className="space-y-1 min-w-0 flex-1">
                              {p.sponsored && adminSettings.enableSponsoredLabels ? (
                                <span className="text-[7px] font-mono bg-amber-500/20 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded uppercase font-black">
                                  Sponsored
                                </span>
                              ) : (
                                <span className="text-[7px] font-mono bg-[#e840f3]/25 text-pink-300 border border-[#e840f3]/20 px-1.5 py-0.5 rounded uppercase font-bold">
                                  Product Result
                                </span>
                              )}

                              <h4 className="text-[11px] font-sans font-black text-white uppercase leading-tight truncate">
                                {p.title}
                              </h4>
                              <p className="text-[9px] font-mono text-slate-500 leading-tight line-clamp-2">
                                {p.description}
                              </p>
                            </div>
                          </div>

                          <div className="mt-3 pt-2.5 border-t border-zinc-900/60 flex items-end justify-between">
                            <div>
                              {adminSettings.enablePriceDisplay && (
                                <div className="flex items-baseline gap-1.5">
                                  <span className="text-xs font-bold text-amber-500 tracking-tight font-mono">
                                    {p.currency === 'GBP' ? '£' : '$'}{p.price.toFixed(2)}
                                  </span>
                                  {p.originalPrice && p.originalPrice > p.price && (
                                    <span className="text-[9px] line-through text-slate-600 font-mono">
                                      {p.currency === 'GBP' ? '£' : '$'}{p.originalPrice.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              )}
                              <span className="text-[8px] font-mono text-zinc-500 uppercase block mt-0.5">
                                via {p.retailer}
                              </span>
                            </div>

                            {p.discountPercent ? (
                              <span className="text-[8px] font-mono bg-red-950/40 text-red-500 px-2 py-0.5 rounded border border-red-500/20 uppercase font-black">
                                -{p.discountPercent}% OFF
                              </span>
                            ) : null}
                          </div>

                          {/* Quick Trigger Options */}
                          <div 
                            className="mt-3 flex gap-1 justify-between text-[9px] font-mono border-t border-zinc-900 pt-2.5"
                            onClick={e => e.stopPropagation()}
                          >
                            <button
                              onClick={() => {
                                setClickedProduct(p);
                                setPinnedProduct(p);
                              }}
                              className="p-1 px-1.5 text-cyan-400 bg-cyan-950/20 rounded border border-cyan-500/10 hover:border-cyan-400 hover:bg-cyan-950/45 text-[8px] transition-all cursor-pointer flex items-center gap-1 uppercase"
                            >
                              <Eye size={10} /> Preview
                            </button>

                            {adminSettings.enableComparisonMode && (
                              <button
                                onClick={() => handleSaveProduct(p)}
                                className={`p-1 px-1.5 rounded border text-[8px] transition-all cursor-pointer flex items-center gap-1 uppercase ${
                                  isSaved 
                                    ? 'text-emerald-400 bg-emerald-950/20 border-emerald-500/40' 
                                    : 'text-slate-400 bg-slate-950 border-zinc-800 hover:border-[#e840f3]'
                                }`}
                              >
                                {isSaved ? <Check size={10} /> : <Save size={10} />}
                                {isSaved ? 'Saved' : 'Save'}
                              </button>
                            )}

                            {adminSettings.enableSendToAgent && (
                              <button
                                onClick={() => handleSendToAgentClick(p)}
                                className="p-1 px-1.5 text-purple-300 bg-purple-950/20 rounded border border-purple-500/10 hover:border-purple-400 hover:bg-purple-950/40 text-[8px] transition-all cursor-pointer flex items-center gap-1 uppercase"
                              >
                                <Send size={10} /> Agent
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* TASK 7 - Comparison Control Button Bar */}
              {savedProducts.length >= 2 && adminSettings.enableComparisonMode && (
                <div className="border border-cyan-500/20 bg-[#020510]/95 p-4 rounded-xl flex items-center justify-between gap-4 font-mono text-xs shadow-[0_0_20px_rgba(6,182,212,0.1)]">
                  <div className="flex items-center gap-2">
                    <ArrowLeftRight className="w-4 h-4 text-cyan-400" />
                    <div>
                      <span className="text-[10px] text-slate-500 block uppercase">PRODUCT COMPARE DECK:</span>
                      <span className="text-white font-bold">{savedProducts.length} items staged for analysis</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSavedProducts([]);
                        setComparedProducts([]);
                        setShowComparePanel(false);
                      }}
                      className="py-1.5 px-3 bg-zinc-900 hover:bg-zinc-805 border border-zinc-804 hover:border-red-500/30 text-red-400 rounded-lg text-[9px] font-mono uppercase tracking-wider transition-colors cursor-pointer"
                    >
                      Clear Saved
                    </button>

                    <button
                      onClick={handleTriggerCompare}
                      disabled={isComparing}
                      className="py-1.5 px-3 bg-[#e840f3]/25 hover:bg-[#e840f3]/40 text-[#e840f3] border border-[#e840f3]/40 rounded-lg text-[9px] font-mono uppercase font-black tracking-wider transition-colors cursor-pointer flex items-center gap-1.5"
                    >
                      {isComparing ? 'Comparing...' : 'Compare Products'}
                    </button>
                  </div>
                </div>
              )}

              {/* TASK 7 - Comparative Grid Panel Details */}
              <AnimatePresence>
                {showComparePanel && comparedProducts.length >= 2 && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 30 }}
                    className="border border-cyan-500/20 bg-[#01040f] rounded-2xl p-5 md:p-6 space-y-4 shadow-[0_0_25px_rgba(6,182,212,0.15)] select-text"
                  >
                    <div className="flex items-center justify-between border-b border-indigo-950 pb-3">
                      <div className="flex items-center gap-2">
                        <ArrowLeftRight className="w-5 h-5 text-cyan-400" />
                        <h3 className="text-sm font-sans font-black text-white uppercase tracking-wider">
                          Nexus Product Compare Panel
                        </h3>
                      </div>
                      <button
                        onClick={() => setShowComparePanel(false)}
                        className="p-1 rounded text-slate-500 hover:text-white cursor-pointer"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-left font-mono text-[10px] text-slate-300 border-collapse">
                        <thead>
                          <tr className="border-b border-indigo-950 text-slate-550">
                            <th className="py-2.5 px-3 font-extrabold uppercase">Compare Fields</th>
                            {comparedProducts.map(p => (
                              <th key={p.id} className="py-2.5 px-3 text-white font-extrabold uppercase tracking-tight">
                                {p.title}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-zinc-900 bg-black/10">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Product</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3">
                                <img src={p.imageUrl} alt={p.title} referrerPolicy="no-referrer" className="w-10 h-10 object-cover rounded-md border border-zinc-800" />
                              </td>
                            ))}
                          </tr>

                          <tr className="border-b border-zinc-900 bg-black/20">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Price</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3 font-bold text-amber-400">
                                {p.currency === 'GBP' ? '£' : '$'}{p.price.toFixed(2)}
                              </td>
                            ))}
                          </tr>

                          <tr className="border-b border-zinc-900 bg-black/10">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Retailer</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3 text-[#e840f3]">
                                {p.retailer}
                              </td>
                            ))}
                          </tr>

                          <tr className="border-b border-zinc-900 bg-black/20">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Discount</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3 text-red-400">
                                {p.discountPercent ? `-${p.discountPercent}%` : 'None'}
                              </td>
                            ))}
                          </tr>

                          <tr className="border-b border-zinc-900 bg-black/10">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Delivery</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3 text-slate-400">
                                {p.deliveryNote || 'Standard Courier'}
                              </td>
                            ))}
                          </tr>

                          <tr className="border-b border-zinc-900 bg-black/20">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Rating</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3 text-amber-400">
                                {p.rating ? `★ ${p.rating.toFixed(1)} / 5.0` : 'N/A'}
                              </td>
                            ))}
                          </tr>

                          <tr className="border-b border-zinc-900 bg-black/10">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Pros</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3 text-green-400 leading-normal font-sans">
                                <ul className="list-disc list-inside space-y-0.5">
                                  {p.pros?.map((pro: string, i: number) => (
                                    <li key={i}>{pro}</li>
                                  ))}
                                </ul>
                              </td>
                            ))}
                          </tr>

                          <tr className="border-b border-zinc-900 bg-black/20">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Cons</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3 text-red-500 leading-normal font-sans">
                                <ul className="list-disc list-inside space-y-0.5">
                                  {p.cons?.map((con: string, i: number) => (
                                    <li key={i}>{con}</li>
                                  ))}
                                </ul>
                              </td>
                            ))}
                          </tr>

                          <tr className="border-b border-zinc-900 bg-black/10">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Best For</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3 text-cyan-400 leading-normal font-sans">
                                {p.bestFor}
                              </td>
                            ))}
                          </tr>

                          <tr className="border-b border-zinc-900 bg-black/20">
                            <td className="py-2.5 px-3 font-extrabold uppercase text-slate-400">Product Link</td>
                            {comparedProducts.map(p => (
                              <td key={p.id} className="py-2.5 px-3">
                                <a
                                  href={p.affiliateUrl && adminSettings.enableAffiliateLinks ? p.affiliateUrl : (p.productUrl || "#")}
                                  target="_blank"
                                  className="text-cyan-400 hover:underline flex items-center gap-1 font-sans"
                                >
                                  <ExternalLink size={11} /> Open Link
                                </a>
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="pt-2.5 flex justify-end">
                      <button
                        onClick={handleGenerateReport}
                        className="py-2 px-5 bg-gradient-to-r from-cyan-500 to-[#e840f3] text-black font-mono text-xs font-black uppercase tracking-wider rounded-xl hover:brightness-110 transition-all shadow-[0_0_15px_rgba(6,182,212,0.25)] flex items-center gap-1.5 cursor-pointer"
                      >
                        <Sparkles size={13} /> Generate Product Report
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Top Searches Section (using local storage tracking) */}
              {Object.keys(topSearches).length > 0 && (
                <div className="border border-indigo-950/40 bg-zinc-950/40 p-5 rounded-2xl text-left space-y-3 font-mono">
                  <div className="flex items-center gap-2 border-b border-indigo-950/60 pb-2">
                    <TrendingUp className="w-4 h-4 text-[#e840f3]" />
                    <span className="text-[10px] font-sans font-black text-white uppercase tracking-wider">
                      TOP VERIFIED QUERY ARCHIVES (FREQUENTLY QUERIED)
                    </span>
                  </div>
                  <p className="text-[9px] text-slate-400">
                    Direct database index showing verified search counts. Click on any query pill to trigger immediate system audit:
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {Object.entries(topSearches)
                      .sort((a, b) => (b[1] as number) - (a[1] as number)) // Sort by count descending
                      .slice(0, 5) // Limit to top 5
                      .map(([term, count]) => (
                        <button
                          key={term}
                          type="button"
                          onClick={() => {
                            setQuery(term);
                            handleSearch(term);
                          }}
                          className="px-3 py-1.5 bg-zinc-950 hover:bg-zinc-900 border border-zinc-800 hover:border-cyan-400 text-[10px] text-slate-300 hover:text-white rounded-xl cursor-pointer transition-all flex items-center gap-1.5 font-mono"
                        >
                          <span className="text-cyan-400 font-extrabold">&gt;</span>
                          <span>{term}</span>
                          <span className="text-[8px] bg-[#e840f3]/20 text-pink-300 px-1.5 py-0.2 rounded font-black font-mono">
                            {count} searches
                          </span>
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Regulatory Compliance Splitting Footer */}
              <div className="border border-white/5 bg-zinc-950/50 p-4 rounded-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-[9px] font-mono select-text text-slate-500">
                <div className="space-y-1">
                  <span className="text-slate-400 font-extrabold uppercase tracking-wide block">🌐 1% 3% Community Allocation DISPATCH DECK:</span>
                  <p>Inward settlement of 1p processed instantly to Secure Payment Provider Sort: 04-**-** | Account: ******15.</p>
                  <p>Charity Split: 33.33% Dunfermline (Cat-01) | 33.33% Global Target Fund | 33.33% UK National Fund.</p>
                </div>
                
                <div className="shrink-0 space-y-1">
                  <span className="text-slate-400 font-extrabold block uppercase">SYSTEM CHANNELS:</span>
                  <div>• <strong className="text-slate-300">08000 622827</strong> (Voice Line)</div>
                  <div>• <strong className="text-slate-300">Direct Contact</strong> 07359******</div>
                </div>
              </div>

            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-20 border border-indigo-950/30 bg-black/30 rounded-3xl flex flex-col items-center justify-center text-center gap-4 px-6"
            >
              <div className="p-4 bg-indigo-950/30 border border-indigo-500/20 rounded-full animate-pulse">
                <Globe className="w-8 h-8 text-cyan-400" />
              </div>
              
              <div className="space-y-2 max-w-md">
                <h4 className="text-sm font-sans font-black uppercase text-white tracking-widest">
                  STANDBY: MULTIVERSAL ENGINE IDLE
                </h4>
                <p className="text-[10px] font-mono text-slate-500 leading-relaxed">
                  Enter any search directive in the prompt controller above to probe our NexoraX 4-dimensional matrix logs. Verified and tracked on-ledger.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* TASK 5 - Nexus Product Action Pop-up (Responsive Desktop & Mobile bottom sheet) */}
      <AnimatePresence>
        {(pinnedProduct || clickedProduct || hoveredProduct) && adminSettings.enableProductPopups && (
          (() => {
            const activeProduct = pinnedProduct || clickedProduct || hoveredProduct;
            return (
              <div className="fixed bottom-0 left-0 right-0 md:bottom-6 md:right-6 md:left-auto md:max-w-xs w-full z-50 p-4 md:p-0">
                <motion.div
                  initial={{ y: 80, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 80, opacity: 0 }}
                  className="bg-[#030612]/95 border-2 border-cyan-400 rounded-3xl p-5 md:p-6 shadow-[0_0_30px_rgba(6,182,212,0.3)] select-text flex flex-col gap-4 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-tr from-purple-950/10 via-black to-cyan-950/10 opacity-30 pointer-events-none" />

                  <div className="relative z-10 flex items-center justify-between border-b border-indigo-950 pb-2">
                    <div className="flex flex-col">
                      <span className="text-[9px] font-mono text-[#e840f3] font-black uppercase tracking-widest leading-none">
                        {pinnedProduct ? "📌 Pinned Product" : hoveredProduct ? "⚡ Live Hover Summary" : "🔥 Product Options"}
                      </span>
                      <h3 className="text-[11px] font-mono font-bold text-white uppercase tracking-tight mt-1">
                        Nexus Product Action Pop-up
                      </h3>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => {
                          if (pinnedProduct) {
                            setPinnedProduct(null);
                          } else {
                            setPinnedProduct(activeProduct);
                          }
                        }}
                        className={`p-1 rounded cursor-pointer transition-all ${
                          pinnedProduct ? 'text-cyan-400 bg-cyan-950/40 border border-cyan-500/30' : 'text-slate-500 hover:text-white'
                        }`}
                        title="Pin Smart Card"
                      >
                        <Pin size={12} className={pinnedProduct ? 'fill-cyan-400' : ''} />
                      </button>

                      <button
                        onClick={() => {
                          setPinnedProduct(null);
                          setClickedProduct(null);
                          setHoveredProduct(null);
                        }}
                        className="p-1 rounded text-slate-500 hover:text-white cursor-pointer"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="relative z-10 flex gap-3.5 items-start">
                    {activeProduct.imageUrl && (
                      <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 border border-zinc-900 bg-zinc-950/50 p-0.5">
                        <img src={activeProduct.imageUrl} alt={activeProduct.title} referrerPolicy="no-referrer" className="w-full h-full object-cover rounded-lg" />
                      </div>
                    )}

                    <div className="space-y-1 min-w-0 flex-1">
                      <h4 className="text-[11px] font-sans font-black text-white uppercase tracking-tight leading-snug line-clamp-2">
                        {activeProduct.title}
                      </h4>
                      <p className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest font-bold">
                        Category: <strong className="text-pink-300">{activeProduct.category}</strong>
                      </p>
                      {activeProduct.rating && (
                        <div className="flex items-center gap-1 text-[9px] font-mono text-amber-500 font-bold">
                          <span>★</span>
                          <span>{activeProduct.rating.toFixed(1)} / 5.0</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="relative z-10 text-[10px] font-mono text-slate-300 leading-normal bg-black/45 p-2.5 rounded-lg border border-zinc-900">
                    {activeProduct.description}
                  </p>

                  {adminSettings.enablePriceDisplay && (
                    <div className="relative z-10 flex items-center justify-between bg-zinc-950/40 p-2.5 rounded-xl border border-zinc-900">
                      <div className="flex flex-col">
                        <span className="text-[8px] font-mono text-slate-500 uppercase font-black">PRICE SUMMARY:</span>
                        <span className="text-base font-black text-amber-400 font-mono tracking-tight leading-none mt-0.5">
                          {activeProduct.currency === 'GBP' ? '£' : '$'}{activeProduct.price.toFixed(2)}
                        </span>
                      </div>

                      {activeProduct.discountPercent ? (
                        <div className="text-right">
                          <span className="text-[8px] font-mono bg-red-950/30 text-red-400 px-2 py-0.5 rounded border border-red-500/20 uppercase font-black">
                            -{activeProduct.discountPercent}% Discount
                          </span>
                          {activeProduct.originalPrice && (
                            <span className="text-[8px] font-mono text-slate-500 line-through block mt-0.5">
                              {activeProduct.currency === 'GBP' ? '£' : '$'}{activeProduct.originalPrice.toFixed(2)}
                            </span>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}

                  {activeProduct.deliveryNote && (
                    <div className="relative z-10 text-[8px] font-mono text-slate-400 bg-black/20 px-2 py-1 rounded">
                      📦 Delivery Offer: {activeProduct.deliveryNote}
                    </div>
                  )}

                  <div className="relative z-10 p-2 bg-indigo-950/5 border border-indigo-950/20 rounded-lg text-[8px] font-mono text-slate-500 space-y-1">
                    <span className="text-slate-400 font-bold block uppercase">
                      {activeProduct.sponsored ? "📢 Sponsored Partner Placement" : "🔗 External Reference Source"}
                    </span>
                    <p>Prices and availability may change. Check the retailer before purchasing.</p>
                  </div>

                  <div className="relative z-10 flex flex-col gap-2 pt-1">
                    <a
                      href={activeProduct.affiliateUrl && adminSettings.enableAffiliateLinks ? activeProduct.affiliateUrl : (activeProduct.productUrl || "#")}
                      target="_blank"
                      onClick={() => {
                        alert(`Redirecting node to verified terminal: ${activeProduct.retailer}`);
                      }}
                      className="w-full text-center py-2.5 bg-gradient-to-r from-cyan-500 to-[#e840f3] text-black font-mono text-xs font-black uppercase tracking-wider rounded-xl hover:brightness-110 transition-all shadow-[0_0_15px_rgba(6,182,212,0.25)] flex items-center justify-center gap-1.5"
                    >
                      <ShoppingCart size={13} />
                      {getCtaLabel(activeProduct, false)}
                    </a>

                    <div className="grid grid-cols-2 gap-2 text-[8px] font-mono">
                      {adminSettings.enableComparisonMode && (
                        <button
                          onClick={() => handleSaveProduct(activeProduct)}
                          className="py-1.5 bg-black hover:bg-zinc-900 border border-zinc-800 hover:border-[#e840f3] text-slate-300 hover:text-white rounded-lg transition-colors cursor-pointer text-center uppercase"
                        >
                          Save Product
                        </button>
                      )}
                      {adminSettings.enableSendToAgent && (
                        <button
                          onClick={() => handleSendToAgentClick(activeProduct)}
                          className="py-1.5 bg-black hover:bg-zinc-900 border border-zinc-800 hover:border-cyan-400 text-slate-300 hover:text-white rounded-lg transition-colors cursor-pointer text-center uppercase"
                        >
                          Send to Agent
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              </div>
            );
          })()
        )}
      </AnimatePresence>

      {/* TASK 7 - Generated Comparison Report Overlay Modal */}
      <AnimatePresence>
        {showReportModal && generatedReport && (
          <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#020510]/95 border-2 border-[#e840f3] rounded-3xl p-6 max-w-2xl w-full text-slate-300 font-mono text-xs space-y-4 shadow-[0_0_40px_rgba(232,64,243,0.25)] relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4">
                <button onClick={() => setShowReportModal(false)} className="text-slate-500 hover:text-white cursor-pointer">
                  <X size={18} />
                </button>
              </div>

              <div className="flex items-center gap-2 border-b border-indigo-950 pb-2">
                <Sparkles className="w-5 h-5 text-[#e840f3] animate-pulse" />
                <h3 className="text-sm font-sans font-black text-white uppercase tracking-wider">
                  Generated Product Report Overview
                </h3>
              </div>

              <div className="bg-black/80 border border-zinc-900 rounded-xl p-5 h-80 overflow-y-auto whitespace-pre-wrap leading-relaxed select-text font-mono text-[11px] text-left">
                {generatedReport}
              </div>

              <div className="flex justify-between items-center text-[9px] text-zinc-550 pt-2 border-t border-indigo-950/60">
                <span>Verification tracking: Secure Invoice Sync NHSB-4487</span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedReport);
                    alert("Report text copied securely to clipboard.");
                  }}
                  className="py-1.5 px-4 bg-[#e840f3]/25 hover:bg-[#e840f3]/40 text-white border border-[#e840f3]/30 rounded-lg text-[9px] font-mono uppercase tracking-wider transition-all cursor-pointer"
                >
                  Copy to Clipboard
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* TASK 6 - Send to Agent Command Dispatcher Drawer Modal */}
      <AnimatePresence>
        {dispatchProduct && (
          <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[#020510]/95 border-2 border-cyan-400 rounded-3xl p-6 max-w-md w-full text-slate-300 font-mono text-xs space-y-4 shadow-[0_0_40px_rgba(6,182,212,0.25)] relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4">
                <button onClick={() => setDispatchProduct(null)} className="text-slate-500 hover:text-white cursor-pointer">
                  <X size={18} />
                </button>
              </div>

              <div className="flex items-center gap-2 border-b border-indigo-950 pb-2">
                <Send className="w-5 h-5 text-cyan-400 animate-pulse" />
                <h3 className="text-sm font-sans font-black text-white uppercase tracking-wider">
                  Nexus Agent Command Dispatcher
                </h3>
              </div>

              <div className="p-3 bg-zinc-950 border border-zinc-905 rounded-lg flex items-center gap-3">
                {dispatchProduct.imageUrl && (
                  <img src={dispatchProduct.imageUrl} alt={dispatchProduct.title} referrerPolicy="no-referrer" className="w-10 h-10 object-cover rounded border border-zinc-800" />
                )}
                <div className="text-left flex-1 min-w-0">
                  <h4 className="text-[11px] text-white font-sans font-bold uppercase truncate">{dispatchProduct.title}</h4>
                  <span className="text-[9px] text-amber-550 font-mono block mt-1">
                    {dispatchProduct.retailer} • {dispatchProduct.currency === 'GBP' ? '£' : '$'}{dispatchProduct.price.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="space-y-3 pt-2 text-left">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] text-slate-500 font-bold uppercase">SELECT AGENT ACTION:</span>
                  <select
                    value={selectedAgentAction}
                    onChange={(e) => setSelectedAgentAction(e.target.value)}
                    className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-white"
                  >
                    <option value="Compare product">Compare product</option>
                    <option value="Create supplier shortlist">Create supplier shortlist</option>
                    <option value="Create product report">Create product report</option>
                    <option value="Create WorkSuite task">Create WorkSuite task</option>
                    <option value="Draft client recommendation">Draft client recommendation</option>
                    <option value="Save to Marketplace watchlist">Save to Marketplace watchlist</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-[10px] text-slate-500 font-bold uppercase">CUSTOM DISPATCH MANDATES:</span>
                  <textarea
                    placeholder="Add custom context or instruction parameters for the agent sandbox..."
                    value={customAgentInstruction}
                    onChange={(e) => setCustomAgentInstruction(e.target.value)}
                    className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-indigo-100 h-20 placeholder-slate-700 outline-none resize-none"
                  />
                </div>
              </div>

              {dispatchLogResult ? (
                <div className="p-3.5 bg-cyan-950/20 border border-cyan-500/25 rounded-xl space-y-2 text-[9px] leading-relaxed text-left">
                  <div className="flex items-center gap-1.5 text-emerald-400 font-bold uppercase">
                    <Check size={12} /> Dispatch Handshake Successful
                  </div>
                  <p className="text-slate-300">{dispatchLogResult.message || `Linked securely to Google Drive NexoraX Resource Library.`}</p>
                  <div className="text-slate-500">AGENT QUEUE ID: <strong className="text-cyan-400">{dispatchLogResult.agentQueueId}</strong></div>
                </div>
              ) : null}

              <div className="flex justify-end gap-2 pt-2 border-t border-indigo-950/60">
                <button
                  onClick={() => setDispatchProduct(null)}
                  className="py-1.5 px-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-slate-300 rounded-lg uppercase text-[9px] cursor-pointer"
                >
                  Close
                </button>
                {!dispatchLogResult && (
                  <button
                    onClick={handleExecuteAgentDispatch}
                    className="py-1.5 px-4 bg-gradient-to-r from-cyan-500 to-[#e840f3] text-[#000] font-mono text-xs font-black uppercase tracking-wider rounded-lg hover:brightness-110 transition-all cursor-pointer"
                  >
                    Execute Dispatch
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
